/**
 * Trunnion AI - Create Agent Wizard
 * Step-by-step agent creation.
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronRight, ChevronLeft, Check } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Card } from '@/components/ui/Card';
import { ClearanceSelector } from '@/components/designer/panels/ClearanceSelector';
import { useCreateAgent } from '@/hooks/queries/useAgents';
import { ROUTES } from '@/config/routes';
import type { CreateAgentRequest, AgentMode, AgentTier } from '@/types';

const steps = ['Identity', 'Configuration', 'Constraints', 'Review'];

export function CreateAgentWizard() {
  const navigate = useNavigate();
  const createAgent = useCreateAgent();
  const [currentStep, setCurrentStep] = useState(0);

  const [formData, setFormData] = useState<CreateAgentRequest>({
    name: '',
    description: '',
    tier: 'specialist',
    mode: 'supervised',
    maxClassification: 'UNCLASSIFIED',
    llmProviderId: '',
    llmModelId: '',
    systemPrompt: '',
    toolWhitelist: [],
    constraints: [],
    tags: [],
  });

  const update = (partial: Partial<CreateAgentRequest>) => {
    setFormData((prev) => ({ ...prev, ...partial }));
  };

  const handleSubmit = () => {
    createAgent.mutate(formData, {
      onSuccess: (agent) => navigate(ROUTES.AGENT_DETAIL(agent.id)),
    });
  };

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      {/* Step indicator */}
      <div className="flex items-center justify-center gap-2">
        {steps.map((step, i) => (
          <div key={step} className="flex items-center gap-2">
            <div
              className={`flex h-8 w-8 items-center justify-center rounded-full text-sm font-medium ${
                i < currentStep
                  ? 'bg-trunnion-gold-500 text-trunnion-slate-950'
                  : i === currentStep
                    ? 'bg-trunnion-gold-500/20 text-trunnion-gold-500 border border-trunnion-gold-500'
                    : 'bg-trunnion-slate-800 text-trunnion-slate-500 border border-trunnion-slate-600'
              }`}
            >
              {i < currentStep ? <Check className="h-4 w-4" /> : i + 1}
            </div>
            <span
              className={`text-sm ${
                i <= currentStep ? 'text-white' : 'text-trunnion-slate-500'
              }`}
            >
              {step}
            </span>
            {i < steps.length - 1 && (
              <div
                className={`w-8 h-px ${
                  i < currentStep ? 'bg-trunnion-gold-500' : 'bg-trunnion-slate-700'
                }`}
              />
            )}
          </div>
        ))}
      </div>

      <Card padding="lg">
        {/* Step 0: Identity */}
        {currentStep === 0 && (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-white">Agent Identity</h3>
            <Input
              label="Name"
              value={formData.name}
              onChange={(e) => update({ name: e.target.value })}
              placeholder="Enter agent name"
            />
            <div>
              <label className="text-sm font-medium text-trunnion-slate-200">
                Description
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => update({ description: e.target.value })}
                rows={3}
                className="mt-1.5 w-full rounded-lg border border-trunnion-slate-600 bg-trunnion-slate-800 px-3 py-2 text-sm text-white placeholder-trunnion-slate-400 focus:outline-none focus:ring-2 focus:ring-trunnion-gold-500 resize-none"
                placeholder="Describe the agent's purpose..."
              />
            </div>
            <Select
              label="Tier"
              value={formData.tier}
              onChange={(e) => update({ tier: e.target.value as AgentTier })}
              options={[
                { value: 'orchestrator', label: 'Orchestrator' },
                { value: 'specialist', label: 'Specialist' },
                { value: 'utility', label: 'Utility' },
              ]}
            />
          </div>
        )}

        {/* Step 1: Configuration */}
        {currentStep === 1 && (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-white">Configuration</h3>
            <Select
              label="Mode"
              value={formData.mode}
              onChange={(e) => update({ mode: e.target.value as AgentMode })}
              options={[
                { value: 'autonomous', label: 'Autonomous' },
                { value: 'supervised', label: 'Supervised' },
                { value: 'manual', label: 'Manual' },
              ]}
            />
            <ClearanceSelector
              value={formData.maxClassification}
              onChange={(maxClassification) => update({ maxClassification })}
            />
            <Input
              label="LLM Provider ID"
              value={formData.llmProviderId}
              onChange={(e) => update({ llmProviderId: e.target.value })}
              placeholder="e.g., openai"
            />
            <Input
              label="LLM Model ID"
              value={formData.llmModelId}
              onChange={(e) => update({ llmModelId: e.target.value })}
              placeholder="e.g., gpt-4"
            />
          </div>
        )}

        {/* Step 2: Constraints */}
        {currentStep === 2 && (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-white">System Prompt & Constraints</h3>
            <div>
              <label className="text-sm font-medium text-trunnion-slate-200">
                System Prompt
              </label>
              <textarea
                value={formData.systemPrompt}
                onChange={(e) => update({ systemPrompt: e.target.value })}
                rows={6}
                className="mt-1.5 w-full rounded-lg border border-trunnion-slate-600 bg-trunnion-slate-800 px-3 py-2 text-sm text-white font-mono placeholder-trunnion-slate-400 focus:outline-none focus:ring-2 focus:ring-trunnion-gold-500 resize-none"
                placeholder="You are an AI agent for..."
              />
            </div>
            <Input
              label="Tags (comma-separated)"
              value={formData.tags.join(', ')}
              onChange={(e) =>
                update({
                  tags: e.target.value
                    .split(',')
                    .map((t) => t.trim())
                    .filter(Boolean),
                })
              }
              placeholder="e.g., analysis, data, security"
            />
          </div>
        )}

        {/* Step 3: Review */}
        {currentStep === 3 && (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-white">Review</h3>
            <div className="space-y-2 text-sm">
              {[
                ['Name', formData.name],
                ['Tier', formData.tier],
                ['Mode', formData.mode],
                ['Classification', formData.maxClassification],
                ['LLM Provider', formData.llmProviderId],
                ['LLM Model', formData.llmModelId],
                ['Tags', formData.tags.join(', ') || 'None'],
              ].map(([label, value]) => (
                <div key={label} className="flex justify-between border-b border-trunnion-slate-700/50 py-2">
                  <span className="text-trunnion-slate-400">{label}</span>
                  <span className="text-white">{value || '-'}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </Card>

      {/* Navigation */}
      <div className="flex items-center justify-between">
        <Button
          variant="secondary"
          icon={<ChevronLeft className="h-4 w-4" />}
          onClick={() => setCurrentStep(Math.max(0, currentStep - 1))}
          disabled={currentStep === 0}
        >
          Previous
        </Button>
        {currentStep < steps.length - 1 ? (
          <Button
            icon={<ChevronRight className="h-4 w-4" />}
            onClick={() => setCurrentStep(currentStep + 1)}
            disabled={currentStep === 0 && !formData.name.trim()}
          >
            Next
          </Button>
        ) : (
          <Button
            onClick={handleSubmit}
            loading={createAgent.isPending}
            icon={<Check className="h-4 w-4" />}
          >
            Create Agent
          </Button>
        )}
      </div>
    </div>
  );
}
